import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Download, ExternalLink, Loader2, Pencil, Trash2 } from 'lucide-react';
import type { DocumentResponse } from '@timesheet/schemas';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { useMobile } from '../../hooks/use-mobile';
import { payslipBalanceService, type PayslipBalanceItem } from '../../services/payslipBalanceService';
import { categoryLabel, formatDate, formatSize, periodLabel } from './documents.utils';

interface DocumentDetailDialogProps {
  doc: DocumentResponse | null;
  open: boolean;
  downloading: boolean;
  onClose: () => void;
  onDownload: (doc: DocumentResponse) => void;
  onOpenInTab: (doc: DocumentResponse) => void;
  onEdit: (doc: DocumentResponse) => void;
  onDelete: (doc: DocumentResponse) => void;
}

const num = (value: number | string | null): number | null => {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
    <span className="shrink-0 text-muted-foreground">{label}</span>
    <span className="min-w-0 text-right font-medium break-words">{value}</span>
  </div>
);

/** Detail popup: metadata, actions, and for the viewer's own monthly payslip the leave balances of that period. */
export const DocumentDetailDialog: React.FC<DocumentDetailDialogProps> = ({
  doc,
  open,
  downloading,
  onClose,
  onDownload,
  onOpenInTab,
  onEdit,
  onDelete,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isMobile = useMobile();
  const [balance, setBalance] = useState<PayslipBalanceItem | null>(null);

  const isOwnMonthlyPayslip =
    !!doc && doc.category === 'BUSTE_PAGA' && doc.payslipType === 'MENSILE' && doc.targetUser?.id === user?.id;

  useEffect(() => {
    if (!open || !doc || !isOwnMonthlyPayslip) {
      setBalance(null);
      return;
    }
    let cancelled = false;
    // Full list + client lookup: the per-period endpoint 404s (and toasts) when there is no balance.
    payslipBalanceService
      .getMy()
      .then((balances) => {
        if (cancelled) return;
        setBalance(balances.find((b) => b.payslipYear === doc.payslipYear && b.payslipMonth === doc.payslipMonth) ?? null);
      })
      .catch(() => setBalance(null));
    return () => {
      cancelled = true;
    };
  }, [open, doc, isOwnMonthlyPayslip]);

  if (!doc) return null;

  const unitLabel = (unit: 'g' | 'h' | null) =>
    unit === 'h' ? t('documents.detail.balances.hours', 'ore') : t('documents.detail.balances.days', 'giorni');
  const balanceRows = balance
    ? [
        { key: 'ferie', label: t('documents.detail.balances.ferie', 'Ferie'), residual: num(balance.ferieResiduo), projection: num(balance.ferieProiezione), unit: balance.ferieUnit },
        { key: 'permessi', label: t('documents.detail.balances.permessi', 'Permessi'), residual: num(balance.permessiResiduo), projection: num(balance.permessiProiezione), unit: balance.permessiUnit },
        { key: 'rol', label: t('documents.detail.balances.rol', 'ROL'), residual: num(balance.rolResiduo), projection: num(balance.rolProiezione), unit: balance.rolUnit },
        { key: 'exFest', label: t('documents.detail.balances.exFest', 'Ex festività'), residual: num(balance.exFestResiduo), projection: num(balance.exFestProiezione), unit: balance.exFestUnit },
      ].filter((row) => row.residual !== null || row.projection !== null)
    : [];

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{categoryLabel(doc.category, t)}</Badge>
            {doc.payslipType && doc.category === 'BUSTE_PAGA' && (
              <Badge variant="outline">{t(`documents.payslipTypes.${doc.payslipType}`, doc.payslipType)}</Badge>
            )}
          </div>
          <DialogTitle className="pr-6">{periodLabel(doc, t, i18n.language)}</DialogTitle>
          {periodLabel(doc, t, i18n.language) !== doc.title && <DialogDescription>{doc.title}</DialogDescription>}
        </DialogHeader>

        <div className="divide-y">
          {doc.targetUser && <Row label={t('documents.detail.person', 'Persona')} value={doc.targetUser.name} />}
          {doc.description && <Row label={t('documents.detail.description', 'Descrizione')} value={doc.description} />}
          <Row label={t('documents.detail.file', 'File')} value={doc.originalFileName} />
          <Row label={t('documents.detail.size', 'Dimensione')} value={formatSize(doc.sizeBytes)} />
          <Row
            label={t('documents.detail.uploadedBy', 'Caricato da')}
            value={`${doc.uploadedBy.name || '—'} · ${formatDate(doc.createdAt, i18n.language)}`}
          />
        </div>

        {isOwnMonthlyPayslip && balanceRows.length > 0 && (
          <div className="rounded-lg border bg-muted/30 p-3">
            <p className="mb-2 text-sm font-semibold">{t('documents.detail.balances.title', 'Saldi ferie e permessi di questo periodo')}</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {balanceRows.map((row) => (
                <React.Fragment key={row.key}>
                  <span className="text-muted-foreground">{row.label}</span>
                  <span className="text-right font-medium tabular-nums">
                    {row.residual !== null ? `${row.residual} ${unitLabel(row.unit)}` : '—'}
                    {row.projection !== null && (
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({t('documents.detail.balances.projection', 'proiezione')} {row.projection})
                      </span>
                    )}
                  </span>
                </React.Fragment>
              ))}
            </div>
            <Link to="/leave-requests" className="mt-2 inline-block text-xs text-orange-600 hover:underline">
              {t('documents.detail.balances.leaveLink', 'Vai alle richieste ferie')}
            </Link>
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <div className="flex gap-2">
            {doc.canEdit && (
              <Button variant="outline" size="sm" onClick={() => onEdit(doc)}>
                <Pencil className="mr-2 h-4 w-4" />
                {t('documents.detail.edit', 'Modifica')}
              </Button>
            )}
            {doc.canDelete && (
              <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => onDelete(doc)}>
                <Trash2 className="mr-2 h-4 w-4" />
                {t('documents.detail.delete', 'Elimina')}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {!isMobile && (
              <Button variant="outline" size="sm" onClick={() => onOpenInTab(doc)} disabled={downloading}>
                <ExternalLink className="mr-2 h-4 w-4" />
                {t('documents.detail.open', 'Apri')}
              </Button>
            )}
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={() => onDownload(doc)} disabled={downloading}>
              {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              {t('documents.detail.download', 'Scarica')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
